import React, { useEffect, useMemo, useReducer } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import VendorSelector from "../../components/Transaction/VendorSelector";
import Notification from "../../components/Common/Notification";

import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import { useTheme } from "../../DataStore/ThemeContext";
import { useRefreshTrans } from "../../DataStore/useRefreshTrans";
import ajaxLedger from "../../util/remote/ajaxLedger";
import SectionTitle from "../../components/Common/SectionTitle";
import AccountingHint from "../../components/Ledger/AccountingHint";

const initialState = {
  loading: false,

  debitAccount: null,   // option object
  creditAccount: null,  // option object
  amount: "",
  description: "",
  transactionRef: "",
  transactionDate: "",

  payee: null,          // option object
  tag: null,            // option object
};

const reducer = (state, action) => {
  switch (action.type) {
    case "RESET_ALL":
      return { ...initialState };
    default:
      return { ...state, [action.type]: action.value };
  }
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => <div className="col-12 col-md-6 col-xl-4">{children}</div>;

const NewGeneralEntryPage = () => {
  const { openSettings, setNotify, currentNotify } = useTheme();
  const { getAllAccounts, accountsCatalogue } = useAccounts();
  const { getTagListing, tagListing } = useTag();
  const refreshBalances = useRefreshTrans();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    getAllAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allAccountsReady = Array.isArray(accountsCatalogue);

  const getFilteredAccounts = useMemo(() => {
    return (allowedTypes = [], excludeId = null) => {
      if (!Array.isArray(accountsCatalogue)) return [];
      return accountsCatalogue
        .filter(
          (item) =>
            allowedTypes.includes(item.account_type) &&
            String(item.account_id) !== String(excludeId || "")
        )
        .map((item) => ({
          label: `${item.account_number} ${item.account_name} (${item.account_type})`,
          value: item.account_id,
        }));
    };
  }, [accountsCatalogue]);

  const debitOptions = useMemo(() => {
    return getFilteredAccounts(["Asset", "Liability", "Expense"], state.creditAccount?.value || null);
  }, [getFilteredAccounts, state.creditAccount]);

  const creditOptions = useMemo(() => {
    return getFilteredAccounts(["Asset", "Liability", "Income"], state.debitAccount?.value || null);
  }, [getFilteredAccounts, state.debitAccount]);

  const tagOptions = useMemo(() => {
    const baseOptions = [
      { label: "Create New Project...", value: "add" },
      { label: "- No project -", value: "0" },
    ];

    const fromServer = Array.isArray(tagListing)
      ? tagListing.map((t) => ({ label: t.tag_name, value: t.tag_id }))
      : [];

    return [...baseOptions, ...fromServer];
  }, [tagListing]);

  const journalHint = useMemo(() => {
    const dr = state.debitAccount?.label || "Debit Account";
    const cr = state.creditAccount?.label || "Credit Account";
    return `Journal rule: Debit ${dr}, Credit ${cr}. Make sure the accounts are correct before posting.`;
  }, [state.debitAccount, state.creditAccount]);

  const onConfirm = async (e) => {
    e.preventDefault();

    if (Number(state.amount) <= 0) {
      return setNotify(<Notification type="danger" message="Enter a valid amount." />);
    }

    if (!state.debitAccount?.value || !state.creditAccount?.value) {
      return setNotify(
        <Notification type="danger" message="Select both debit and credit accounts." />
      );
    }

    if (String(state.debitAccount.value) === String(state.creditAccount.value)) {
      return setNotify(
        <Notification type="danger" message="Debit and credit accounts cannot be the same." />
      );
    }

    dispatch({ type: "loading", value: true });

    const res = await ajaxLedger.postJournalEntry({
      debit_account: state.debitAccount.value,
      credit_account: state.creditAccount.value,
      amount: state.amount,
      transaction_date: state.transactionDate,
      description: state.description,
      payee: state.payee?.value || "",
      transaction_ref: state.transactionRef,
      tag: state.tag?.value || "0",
    });

    dispatch({ type: "loading", value: false });

    if (res.status === "OK") {
      dispatch({ type: "RESET_ALL" });
      setNotify(<Notification type="success" message={res.message} />);
      refreshBalances();
    } else {
      setNotify(<Notification type="danger" message={res.message} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="New Journal Entry"
          description="Create a double-entry journal by specifying debit and credit accounts."
          iconClass="fa fa-book"
        />

        <div className="mb-3">
          <AccountingHint hint={journalHint} />
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            {currentNotify}

            <form onSubmit={onConfirm}>
              {/* ---------- ACCOUNTS ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-university"
                    title="Journal Accounts"
                    note="Choose which account is debited and which account is credited."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Debit Account"
                    placeholder="Select account to debit"
                    leftIcon="fa fa-arrow-circle-down"
                    options={debitOptions}
                    onChange={(item) => dispatch({ type: "debitAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getAllAccounts(true)} />}
                    loading={!allAccountsReady}
                    value={state.debitAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="select"
                    label="Credit Account"
                    placeholder="Select account to credit"
                    leftIcon="fa fa-arrow-circle-up"
                    options={creditOptions}
                    onChange={(item) => dispatch({ type: "creditAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getAllAccounts(true)} />}
                    loading={!allAccountsReady}
                    value={state.creditAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="number"
                    label="Amount"
                    placeholder="Transaction amount"
                    leftIcon="fa fa-credit-card"
                    value={state.amount}
                    onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>
              </div>

              {/* ---------- DETAILS ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-info-circle"
                    title="Transaction Details"
                    note="Optional fields improve audit trail and reconciliation."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="date"
                    label="Transaction Date"
                    placeholder="Transaction Date"
                    leftIcon="fa fa-calendar-alt"
                    value={state.transactionDate}
                    onChange={(e) => dispatch({ type: "transactionDate", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Transaction Reference"
                    placeholder="Reference"
                    leftIcon="fa fa-hashtag"
                    value={state.transactionRef}
                    onChange={(e) => dispatch({ type: "transactionRef", value: e.target.value })}
                    wrap
                  />
                </Col>

                <Col>
                  <VendorSelector
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Vendor", "NewVendor");
                        return;
                      }
                      dispatch({ type: "payee", value: item });
                    }}
                    placeholder="Optional Payee"
                    value={state.payee}
                    wrap
                  />
                </Col>
              </div>

              {/* Description full width */}
              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12">
                  <TextInput
                    type="textarea"
                    label="Transaction Description"
                    placeholder="What is this transaction for?"
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    wrap
                  />
                </div>
              </div>

              {/* ---------- CLASSIFICATION ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-tag"
                    title="Classification"
                    note="Optional: attach a project to improve reporting."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Tag Transaction"
                    placeholder={state.tag?.label || "Optional tag"}
                    leftIcon="fa fa-tag"
                    options={tagOptions}
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Project", "NewTag", null, "Create Project");
                        return;
                      }
                      dispatch({ type: "tag", value: item });
                    }}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getTagListing(true)} />}
                    loading={!Array.isArray(tagListing)}
                    value={state.tag}
                    wrap
                  />
                </Col>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={state.loading}>
                  {state.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Post Journal Entry
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  onClick={() => dispatch({ type: "RESET_ALL" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Reset Form
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Debits increase Assets/Expenses and decrease Liabilities/Income (depending on account type).
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default NewGeneralEntryPage;
